import * as React from "react";
import {
  AriaButtonProps,
  AriaMenuProps,
  Placement,
  mergeProps,
  useMenu,
  useMenuItem,
  useMenuSection,
  useMenuTrigger,
  usePress,
  useSeparator,
} from "react-aria";
import {
  Item,
  ItemProps,
  MenuTriggerProps,
  MenuTriggerState,
  Node,
  Section,
  SectionProps,
  TreeState,
  useMenuTriggerState,
  useTreeState,
} from "react-stately";
import { Flex } from "../flex";
import { Button } from "../button";
import { InternalPopover as Popover } from "../internal-popover";
import { useTheme } from "../theme";
import { cx } from "../../utils/dom";
import { StyleProps } from "../../utils/types";
import { Text } from "../text";
import { Color } from "../../utils/theme";
import { HiCheck } from "react-icons/hi";

interface DropdownProps extends MenuTriggerProps {
  something?: string;
}
interface DropdownContextValue {
  triggerState: MenuTriggerState;
  triggerRef: React.MutableRefObject<HTMLElement>;
  triggerProps: AriaButtonProps;
  menuProps: any;
}
const DropdownContext = React.createContext<DropdownContextValue | null>(null);

export function DropdownMenu({
  children,
  ...props
}: React.PropsWithChildren<DropdownProps>) {
  const triggerRef = React.useRef<HTMLElement>(null);
  const triggerState = useMenuTriggerState(props);

  const { menuProps, menuTriggerProps: triggerProps } = useMenuTrigger(
    props,
    triggerState,
    triggerRef
  );

  return (
    <DropdownContext.Provider
      value={{
        triggerRef: triggerRef as React.MutableRefObject<HTMLElement>,
        triggerState,
        triggerProps,
        menuProps,
      }}
    >
      {children}
    </DropdownContext.Provider>
  );
}

interface DropdownTriggerProps extends StyleProps {
  asChild?: boolean;
  useClickEvent?: boolean;
}

export function DropdownTrigger({
  asChild,
  children,
  useClickEvent,
}: React.PropsWithChildren<DropdownTriggerProps>) {
  const ctx = React.useContext(DropdownContext);

  const { pressProps, isPressed } = usePress({
    ...ctx?.triggerProps,
    ref: ctx?.triggerRef,
  });

  const triggerProps = useClickEvent ? pressProps : ctx?.triggerProps;

  if (asChild) {
    const child = React.Children.only(children);
    return React.cloneElement(child as React.ReactElement, {
      ...triggerProps,
      ref: ctx?.triggerRef,
      "data-pressed": isPressed,
    });
  }

  return (
    <Button
      {...ctx?.triggerProps}
      ref={ctx?.triggerRef as React.RefObject<HTMLButtonElement>}
    >
      {children}
    </Button>
  );
}

interface MenuProps<T> extends AriaMenuProps<T>, StyleProps {
  menuHeader?: React.ReactNode | (() => React.ReactNode);
  menuFooter?: React.ReactNode | (() => React.ReactNode);
  minWidth?: number | string;
  maxHeight?: number | string;
  maxWidth?: number | string;
  placement?: Placement;
  offset?: number;
  color?: Color;
}

export function Menu<T extends object>({
  placement = "bottom start",
  offset = 8,
  ...props
}: MenuProps<T>) {
  const ctx = React.useContext(DropdownContext);

  if (!ctx?.triggerState.isOpen) {
    return null;
  }

  return (
    <Popover
      placement={placement}
      offset={offset}
      state={ctx?.triggerState}
      triggerRef={ctx?.triggerRef}
    >
      <MenuInner {...mergeProps(props, ctx.menuProps)} />
    </Popover>
  );
}

function MenuInner<T extends object>({
  menuHeader,
  menuFooter,
  className,
  color,
  maxHeight = 300,
  minWidth = 200,
  maxWidth,
  ...props
}: MenuProps<T>) {
  const state = useTreeState(props);
  const ref = React.useRef(null);

  const { menuProps } = useMenu(
    {
      ...props,
      autoFocus: true,
      isVirtualized: true,
    },
    state,
    ref
  );
  const theme = useTheme();

  function resolvedMenuHeader() {
    if (typeof menuHeader === "function") {
      return menuHeader();
    }
    return menuHeader;
  }

  function resolvedMenuFooter() {
    if (typeof menuFooter === "function") {
      return menuFooter();
    }
    return menuFooter;
  }

  return (
    <Flex
      stack
      ref={ref}
      {...menuProps}
      data-color={color ?? theme?.color ?? "violet"}
      data-radius={theme?.radius ?? "lg"}
      className={cx("p-list-box-wrapper", className)}
      style={{
        minWidth: `max(200px, ${`${minWidth}`.replace("px", "")}px)`,
        maxWidth: `${`${maxWidth}`.replace("px", "")}px`,
        maxHeight: `${`${maxHeight}`.replace("px", "")}px`,
      }}
    >
      {resolvedMenuHeader()}
      <ul>
        {[...state.collection].map((item) => {
          if (item.type === "section") {
            return (
              <MenuSectionInner
                state={state}
                section={item}
                key={item.key}
                {...item.props}
              />
            );
          }

          return (
            <MenuItemInner
              {...item.props}
              state={state}
              item={item}
              key={item.key}
            />
          );
        })}
      </ul>

      {resolvedMenuFooter()}
    </Flex>
  );
}

interface MenuItemProps<T extends object> {
  state: TreeState<T>;
  item: Node<T>;
}

interface MenuItemRenderProps {
  icon?: React.ReactNode;
  label?: React.ReactNode;
  description?: React.ReactNode;
  asChild?: boolean;
  className?: string;
  style?: React.CSSProperties;
  isReadOnly?: boolean;
  color?: Color;
  onAction?: () => void;
}

function MenuItemInner<T extends object>({
  state,
  item,
  ...otherProps
}: MenuItemProps<T> & MenuItemRenderProps) {
  const ref = React.useRef(null);
  const {
    menuItemProps,
    isSelected,
    isFocused,
    isPressed,
    isDisabled,
    descriptionProps,
  } = useMenuItem({ key: item?.key }, state, ref);
  const ElementType: React.ElementType = item.props.href ? "a" : "div";

  const { className, style, isReadOnly, icon, color, description, label } =
    otherProps;

  return (
    <ElementType
      ref={ref}
      className={cx(className, "p-list-option p-menu-item")}
      data-selected={isSelected}
      data-focused={isFocused}
      data-pressed={isPressed}
      data-disabled={isDisabled}
      data-readonly={isReadOnly}
      style={style}
      data-color={color}
      {...menuItemProps}
    >
      <Flex align="center">
        {icon && (
          <Flex className="p-list-option-icon-wrapper">
            {React.isValidElement(icon) &&
              React.cloneElement(icon as React.ReactElement, {
                className: "p-list-option-icon",
                size: 18,
                ...(icon as React.ReactElement)?.props,
              })}
          </Flex>
        )}

        <Flex gap="3xs" stack>
          <Text as="div" color="black">
            {label ?? item.rendered}
          </Text>
          {description && (
            <Text as="div" lineHeight="base" {...descriptionProps}>
              {description}
            </Text>
          )}
        </Flex>
      </Flex>
      {isSelected && <HiCheck className="selected-marker" />}
    </ElementType>
  );
}

interface MenuSectionProps<T extends object> {
  section: Node<T>;
  state: TreeState<T>;
}

function MenuSectionInner<T extends object>({
  state,
  section,
  className,
  style,
}: MenuSectionProps<T> & MenuItemRenderProps) {
  const { itemProps, headingProps, groupProps } = useMenuSection({
    heading: section?.rendered,
    "aria-label": section?.["aria-label"],
  });

  const { separatorProps } = useSeparator({ elementType: "li" });

  function getChildren() {
    if (state?.collection.getChildren) {
      return [...state.collection.getChildren(section.key)];
    }
    return [...section.childNodes];
  }

  return (
    <Flex
      stack
      as="ul"
      style={style}
      className={cx(className, "p-list-section")}
    >
      {section.key !== state.collection.getFirstKey() && (
        <li className="p-list-separator" {...separatorProps} />
      )}
      <li {...itemProps}>
        {section?.rendered && (
          <Text as="h4" className="p-list-section-header" {...headingProps}>
            {section?.rendered}
          </Text>
        )}

        <ul {...groupProps}>
          {getChildren().map((item) => {
            return (
              <MenuItemInner
                {...item.props}
                state={state}
                item={item}
                key={item.key}
              />
            );
          })}
        </ul>
      </li>
    </Flex>
  );
}

export const MenuItem = Item as <T>(
  props: ItemProps<T> & MenuItemRenderProps
) => JSX.Element;

export const MenuSection = Section as <T>(
  props: SectionProps<T> & MenuItemRenderProps
) => JSX.Element;
